import React, { useState, useEffect, useMemo } from "react";
import { Typography, Box, Grid, Container } from "@mui/material";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import StaticPoolHeaderBar from "../PoolHeaderBar/StaticPoolHeaderBar";
import MyPoolsCard from "../GamePageComponents/MyPoolsCard/MyPoolsCard";
import { useGetPoolHallData } from "../../hooks/pools/useGetPoolHallData";
import { useGetUserData } from "../BirdiePool/hooks";
import { getUserDataFromLocalStorage } from "../../redux/actions/loginImport";
import { useLocation, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

const PoolHallContainer = () => {
    // Your existing hooks for fetching data
    const { mutate: fetchPoolHallData, data: poolHallData, loading: poolHallLoading, error: poolHallError } = useGetPoolHallData();
    const { mutate: fetchUserData, data: userData, loading: userLoading, error: userError } = useGetUserData();
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const location = useLocation();
    const navigate = useNavigate();
    
    // Use useMemo to prevent queryParams from being recreated on every render
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    
    // Initialize with 'all' as the default
    const defaultLeagueId = 'all';
    const leagueParam = queryParams.get('league') || defaultLeagueId;
    
    // Initialize league state from URL or default
    const [selectedLeague, setSelectedLeague] = useState(leagueParam);
    
    const myUserId = getUserDataFromLocalStorage()?.user_id;

    // Process pool data to extract all leagues and prepare all pools with button states
    const { leagueCategories, allPools, mainCategory } = useMemo(() => {
        if (!poolHallData || poolHallData.length === 0) {
            return { 
                leagueCategories: [], 
                allPools: [],
                mainCategory: null
            };
        }

        // Get the main category
        const mainCat = poolHallData[0];
        
        // Create a set to hold unique league keys
        const uniqueLeagues = new Set();
        const poolsWithButtonState = [];

        // Get current date in America/New_York timezone
        const now = DateTime.now().setZone("America/New_York");

        // Extract all leagues from pool data and prepare pools with button states
        if (mainCat && mainCat.pools) {
            mainCat.pools.forEach(pool => {
                // Add league to uniqueLeagues if it exists
                if (pool.league) {
                    uniqueLeagues.add(pool.league);
                }

                // Determine button state based on dates
                let buttonInfo = {
                    ...pool
                };

                // Delete explicit button text properties as they will be calculated dynamically
                delete buttonInfo.gameMemberButtonText;
                delete buttonInfo.nonGameMemberButtonText;

                // Parse dates with Luxon in America/New_York timezone
                const openDate = pool.openDate ? 
                    DateTime.fromISO(pool.openDate, { zone: "America/New_York" }) : null;
                const closeDate = pool.closeDate ? 
                    DateTime.fromISO(pool.closeDate, { zone: "America/New_York" }) : null;

                // Calculate button states without adding sort order
                if (openDate && now < openDate) {
                    // Before open date: Not open yet
                    buttonInfo._nonMemberButtonText = "Coming Soon";
                    buttonInfo._showArrow = false;
                } else if (closeDate && now >= closeDate) {
                    // After close date: Closed
                    buttonInfo._nonMemberButtonText = "Pool Home";
                    buttonInfo._showArrow = true;
                } else {
                    // Between open and close date: Open but not closed
                    buttonInfo._nonMemberButtonText = "Join Pool";
                    buttonInfo._showArrow = true;
                }

                // Member button is always "Pool Home"
                buttonInfo._memberButtonText = "Pool Home";

                // Add to all pools array
                poolsWithButtonState.push(buttonInfo);
            });
        }

        // Convert Set to Array
        const leaguesArray = Array.from(uniqueLeagues);

        // Create league categories including the "All" option
        const categories = [
            {
                id: 'all',
                displayName: 'All',
            },
            ...leaguesArray.map(league => ({
                id: league,
                displayName: league,
            }))
        ];

        // No sorting applied - use pools in the original order they came in
        const poolsInOriginalOrder = [...poolsWithButtonState];

        return { 
            leagueCategories: categories,
            allPools: poolsInOriginalOrder,
            mainCategory: mainCat
        };
    }, [poolHallData]);

    // Ensure league parameter is always in URL and is valid
    useEffect(() => {
        if (leagueCategories.length > 0) {
            // If league is missing from URL or invalid, add it with default value
            const validLeagues = leagueCategories.map(cat => cat.id);
            if (!queryParams.has('league') || !validLeagues.includes(queryParams.get('league'))) {
                queryParams.set('league', defaultLeagueId);
                navigate({ search: queryParams.toString() }, { replace: true });
                setSelectedLeague(defaultLeagueId);
            }
        }
    }, [leagueCategories, location.search, navigate, queryParams, defaultLeagueId]);
    
    // Update URL when league changes
    const handleLeagueChange = (newLeague) => {
        queryParams.set('league', newLeague);
        navigate({ search: queryParams.toString() });
        setSelectedLeague(newLeague);
    };

    // Use Effect to fetch user data (if myUserId is available)
    useEffect(() => {
        if (myUserId) {
            fetchUserData(myUserId);
        }
    }, [fetchUserData, myUserId]);

    // Use Effect to fetch pool hall data
    useEffect(() => {
        fetchPoolHallData();
    }, [fetchPoolHallData]);

    const activePools = userData?.Games?.filter(game => game.masterPoolId && game.status === 'active') || [];

    // Filter pools based on selected league
    const currentPools = selectedLeague === 'all' 
        ? allPools 
        : allPools.filter(pool => pool.league === selectedLeague);

    // Render page header - always using the main category data
    const renderPageHeader = () => {
        if (!mainCategory) return null;
        
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    padding: '16px',
                }}
            >
                {mainCategory.headerImage && (
                    <img
                        src={mainCategory.headerImage}
                        alt={mainCategory.headerAlt || mainCategory.displayName}
                        style={{ 
                            height: isMobile ? '100px' : '200px', 
                            width: 'auto', 
                            objectFit: 'contain',
                            margin: '0 auto',
                        }}
                    />
                )}
                {mainCategory.headerText && (
                    <Typography
                        sx={{
                            fontSize: isMobile ? '16px' : '24px',
                            fontWeight: '700',
                            textAlign: 'center',
                        }}
                    >
                        {mainCategory.headerText}
                    </Typography>
                )}
            </Box>
        );
    };

    // Render cards based on currentPools
    const renderPoolCards = () => {
        if (!currentPools.length) {
            return (
                <LoadingScreen />
            );
        }

        return (
            <Grid 
                container 
                spacing={3}
                sx={{ 
                    mt: 0.5,
                    justifyContent: 'center',
                    px: 3.5
                }}
            >
                {currentPools.map((pool, index) => {
                    // For each pool, pass the modified pool info with button states
                    const modifiedPoolInfo = {
                        ...pool,
                        // Use our calculated button states
                        gameMemberButtonText: pool._memberButtonText,
                        nonGameMemberButtonText: pool._nonMemberButtonText,
                        showArrow: pool._showArrow
                    };

                    return (
                        <Grid 
                            item 
                            xs={12}
                            sx={{ 
                                display: 'flex',
                                justifyContent: 'center',
                                [theme.breakpoints.up(725)]: {
                                    width: '50%',
                                    maxWidth: '50%',
                                    flexBasis: '50%'
                                },
                                [theme.breakpoints.up(1100)]: {
                                    width: '33.333%',
                                    maxWidth: '33.333%',
                                    flexBasis: '33.333%'
                                }
                            }}
                            key={index}
                        >
                            <MyPoolsCard 
                                poolInfo={modifiedPoolInfo} 
                                activePools={activePools}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        );
    };
    
    return (
        <div>
            <StaticPoolHeaderBar navigationText="Home" headerText="Pool Hall" linkForNavigation="/" />
            
            {renderPageHeader()}
            
            <Box sx={{ marginBottom: '32px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center', // Center the container itself
                    margin: '15px auto',
                    maxWidth: '100%',
                }}>
                    <div style={{
                        display: 'flex',
                        maxWidth: 'min(90%, 800px)', // Limit width but allow it to be responsive
                        overflowX: 'auto', // Enable horizontal scrolling
                        scrollbarWidth: 'thin', // For Firefox
                        WebkitOverflowScrolling: 'touch', // For smooth scrolling on iOS
                        padding: '5px 0', // Add some padding for better look
                        msOverflowStyle: 'none', // Hide scrollbar in IE and Edge
                        scrollbarColor: 'transparent transparent', // Hide scrollbar in Firefox
                    }}>
                        {leagueCategories.map((category, index) => (
                            <span
                                key={category.id}
                                style={{
                                    display: 'block',
                                    padding: '12px 16px',
                                    marginRight: index === leagueCategories.length - 1 ? '0' : '10px', // Gap between buttons, no margin on last item
                                    borderRadius: '6px',
                                    backgroundColor: selectedLeague === category.id ? '#E8E8E8' : null,
                                    color: '#002129',
                                    fontSize: isMobile ? '12px' : '18px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0 // Prevent buttons from shrinking
                                }}
                                onClick={() => handleLeagueChange(category.id)}
                            >
                                {category.displayName}
                            </span>
                        ))}
                    </div>
                </div>
            </Box>

            <Container 
                maxWidth="xl" 
                sx={{ 
                    px: { xs: 1, sm: 2, md: 3.5 },
                    pb: 3.5,
                }}
            >
                {renderPoolCards()}
            </Container>
        </div>
    );
}

export default PoolHallContainer;